import { useState } from "react";
import type {
	FAQItem,
	ProviderDetailsProps,
} from "../../core/types/tsx/components/Profesional/ProviderDetails.types";
import {
	Check,
	Star,
	ChevronRight,
	SprayCan,
	Globe,
	Hourglass,
	Info,
	User,
	Award,
} from "lucide-react";
import { BadgeIcon } from "../common/BadgeIcon";
import { getImageUri } from "../../core/utils/imageLoad";

// --- TIPOS ---

// Actualizado para coincidir exactamente con los campos de 'reviews_qualified' de tu JSON

// --- Helper para fecha ---
const formatDate = (dateString: string) => {
	if (!dateString) return "";
	try {
		const date = new Date(dateString);
		return date.toLocaleDateString("es-VE", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	} catch (_e) {
		return dateString;
	}
};

const formatServicePrice = (price?: number | null) => {
	if (typeof price !== "number" || !Number.isFinite(price) || price <= 0) {
		return "Consultar";
	}

	return `$${price.toLocaleString("es-VE", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

// --- Sub-componente 1: Acerca de ---
const AboutSection = ({
	name,
	description,
	category,
	categoryIcon,
	categories,
	badges,
	languages,
	experience,
	services,
}: ProviderDetailsProps) => (
	<div className='space-y-6 sm:space-y-8'>
		<div>
			<h3 className='text-lg font-bold text-slate-700 mb-3'>
				{name || "Proveedor"}
			</h3>
			<p className='text-slate-500 leading-relaxed text-sm sm:text-base'>
				{description || "Información del proveedor no disponible."}
			</p>
		</div>

		<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
			{languages && (
				<div className='flex items-start gap-3 p-4 bg-slate-50 rounded-xl'>
					<Globe className='w-6 h-6 text-primary mt-1' />
					<div>
						<h4 className='text-sm font-bold text-slate-700'>Idiomas</h4>
						<p className='text-slate-500 text-sm'>{languages}</p>
					</div>
				</div>
			)}
			{experience !== undefined && experience !== null && (
				<div className='flex items-start gap-3 p-4 bg-slate-50 rounded-xl'>
					<Hourglass className='w-6 h-6 text-primary mt-1' />
					<div>
						<h4 className='text-sm font-bold text-slate-700'>Experiencia</h4>
						<p className='text-slate-500 text-sm'>
							{experience} Año(s) en el sector
						</p>
					</div>
				</div>
			)}
		</div>

		<div>
			<h4 className='text-sm font-bold text-slate-700 mb-3'>Servicios Ofrecidos</h4>
			{services && services.length > 0 ? (
				<ul className='space-y-3'>
					{services.map((srv) => (
						<li
							key={srv.id}
							className='rounded-xl border border-slate-200 bg-white p-3'
						>
							<div className='flex items-start justify-between gap-3'>
								<div className='min-w-0'>
									<div className='flex items-center gap-2 text-slate-600'>
										<Check className='w-4 h-4 text-slate-400 shrink-0' />
										<span className='font-medium'>
											{srv.name_service}
										</span>
									</div>
									{srv.description_service?.trim() ? (
										<p className='mt-1 text-xs text-slate-500 line-clamp-2'>
											{srv.description_service}
										</p>
									) : null}
								</div>
								<span className='shrink-0 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary'>
									{formatServicePrice(srv.base_price)}
								</span>
							</div>
						</li>
					))}
				</ul>
			) : (
				<p className='text-sm text-slate-400 italic'>
					Este proveedor aún no ha registrado servicios.
				</p>
			)}
		</div>

		<div>
			<h4 className='text-sm font-bold text-slate-700 mb-3'>Categoría</h4>
			<div className='flex flex-wrap gap-2'>
				{Array.isArray(categories) && categories.length > 0
					? categories.map((categoryItem, idx) => (
							<span
								key={categoryItem.name || idx}
								className='inline-flex items-center gap-2 bg-secondary text-primary px-4 py-1.5 rounded-full text-sm font-medium max-w-full'
							>
								{categoryItem.icon ? (
									<img
										src={categoryItem.icon}
										alt={`Icono de ${categoryItem.name || "categoría"}`}
										className='w-4 h-4 object-contain'
									/>
								) : (
									<SprayCan className='w-4 h-4' />
								)}
								<span className='truncate'>
									{categoryItem.name || "General"}
								</span>
							</span>
						))
					: Array.isArray(category)
						? category.map((cat, idx) => (
						<span
							key={cat || idx}
							className='inline-flex items-center gap-2 bg-secondary text-primary px-4 py-1.5 rounded-full text-sm font-medium max-w-full'
						>
							{categoryIcon && idx === 0 ? (
								<img
									src={categoryIcon}
									alt={`Icono de ${cat || "categoría"}`}
									className='w-4 h-4 object-contain'
								/>
							) : idx === 0 ? (
								<SprayCan className='w-4 h-4' />
							) : null}
							<span className='truncate'>{cat || "General"}</span>
						</span>
						))
						: (
							<span className='inline-flex items-center gap-2 bg-secondary text-primary px-4 py-1.5 rounded-full text-sm font-medium max-w-full'>
								{categoryIcon ? (
									<img
										src={categoryIcon}
										alt={`Icono de ${category || "categoría"}`}
										className='w-4 h-4 object-contain'
									/>
								) : (
									<SprayCan className='w-4 h-4' />
								)}
								<span className='truncate'>{category || "General"}</span>
							</span>
						)}
			</div>
		</div>

		<div>
			<h4 className='text-sm font-bold text-slate-700 mb-3'>Insignias</h4>
			{badges && badges.length > 0 ? (
				<div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
					{badges.map((badge) => (
						<div
							key={badge.id}
							className='flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-white'
						>
							<div className='w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 p-2 shrink-0'>
								{badge.icon_badge ? (
									<BadgeIcon
										src={badge.icon_badge}
										type={badge.type_badge}
										alt={badge.name_badge}
									/>
								) : (
									<Award className='w-full h-full text-primary' />
								)}
							</div>
							<div className='min-w-0'>
								<p className='text-sm font-semibold text-slate-700'>
									{badge.name_badge}
								</p>
								<p className='text-xs text-slate-500 line-clamp-2 mt-1'>
									{badge.description_badge?.trim() ||
										"Insignia del proveedor"}
								</p>
							</div>
						</div>
					))}
				</div>
			) : (
				<p className='text-sm text-slate-400 italic'>
					Este proveedor no tiene insignias asignadas.
				</p>
			)}
		</div>
	</div>
);

// --- Sub-componente 2: Galería ---
const GallerySection = ({ galleryImages }: { galleryImages?: string[] }) => (
	<div>
		<h3 className='text-lg font-bold text-slate-700 mb-6'>Galería de fotos</h3>
		{galleryImages && galleryImages.length > 0 ? (
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
				{galleryImages.map((url, idx) => (
					<img
						key={`gallery-${idx}`}
						src={url}
						alt={`Trabajo ${idx + 1}`}
						className='w-full h-40 sm:h-48 object-cover rounded-xl'
					/>
				))}
			</div>
		) : (
			<div className='flex items-center gap-2 text-slate-400 bg-slate-50 p-4 rounded-xl'>
				<Info className='w-5 h-5' />
				<span className='text-sm'>
					El proveedor aún no ha subido fotos de sus trabajos.
				</span>
			</div>
		)}
	</div>
);

// --- Sub-componente 3: Reseñas ---
const ReviewsSection = ({ rating, reviews, reviewsList }: ProviderDetailsProps) => {
	const totalReviews = reviews ?? (reviewsList?.length || 0);

	return (
		<div>
			<h3 className='text-lg font-bold text-slate-700 mb-6'>Reseñas</h3>

			{/* Resumen de Calificación */}
			<div className='flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4 mb-6 sm:mb-8 bg-slate-50 p-4 rounded-xl'>
				<span className='text-5xl font-normal text-primary'>
					{rating ?? "0.0"}
				</span>
				<div>
					<div className='flex gap-1 mb-1'>
						{[1, 2, 3, 4, 5].map((s) => (
							<Star
								key={s}
								className={`w-5 h-5 ${
									Number(rating) >= s
										? "text-primary fill-primary"
										: "text-slate-300"
								}`}
							/>
						))}
					</div>
					<p className='text-slate-400 text-sm'>
						{totalReviews} reseñas totales
					</p>
				</div>
			</div>

			{/* Lista de Comentarios Reales */}
			{reviewsList && reviewsList.length > 0 ? (
				<div className='space-y-6 sm:space-y-8 divide-y divide-gray-100'>
					{reviewsList.map((review) => (
						<div
							key={review.id}
							className='flex flex-col gap-3 pt-6 first:pt-0 sm:flex-row sm:gap-4'
						>
							<div className='w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-primary font-bold shrink-0 text-sm overflow-hidden'>
								{review.qualifier?.image_profile ? (
									<img
										src={getImageUri(review.qualifier.image_profile)}
										alt={review.qualifier.names}
										className='w-full h-full object-cover'
										onError={(e) => {
											e.currentTarget.onerror = null;
											e.currentTarget.style.display = "none";
										}}
									/>
								) : (
									<User className='w-5 h-5' />
								)}
							</div>
							<div className='min-w-0'>
								<div className='flex items-center gap-2 mb-1'>
									<span className='font-semibold text-slate-700'>
										{/* Mostrar el nombre del cliente si existe, si no fallback al id */}
										{review.qualifier?.names?.trim()
											? review.qualifier.names
											: `Usuario #${review.qualifier_id}`}
									</span>
								</div>

								{/* Estrellas individuales de la reseña */}
								<div className='flex items-center gap-2 mb-2'>
									<div className='flex'>
										{[1, 2, 3, 4, 5].map((s) => (
											<Star
												key={s}
												className={`w-3 h-3 ${
													review.punctuation >= s
														? "text-primary fill-primary"
														: "text-slate-300"
												}`}
											/>
										))}
									</div>
									<span className='text-xs text-slate-400'>
										{formatDate(review.date_review)}
									</span>
								</div>

								{/* Comentario */}
								<p className='wrap-break-word text-slate-500 text-sm'>
									"{review.comment_review}"
								</p>
							</div>
						</div>
					))}
				</div>
			) : (
				<p className='text-slate-400 italic text-center py-4'>
					Este proveedor aún no tiene reseñas con comentarios.
				</p>
			)}
		</div>
	);
};

// --- Sub-componente 4: FAQ ---
const FAQSection = ({ faq = [] }: { faq?: FAQItem[] }) => {
	const [activeIndex, setActiveIndex] = useState<number | null>(null);

	const toggleFAQ = (index: number) => {
		setActiveIndex(activeIndex === index ? null : index);
	};

	if (!faq || faq.length === 0) {
		return (
			<div>
				<h3 className='text-lg font-bold text-slate-700 mb-4'>
					Preguntas Frecuentes
				</h3>
				<div className='flex items-center gap-2 text-slate-400 bg-slate-50 p-4 rounded-xl'>
					<Info className='w-5 h-5' />
					<span className='text-sm'>
						El proveedor no ha cargado preguntas frecuentes.
					</span>
				</div>
			</div>
		);
	}

	return (
		<div>
			<h3 className='text-lg font-bold text-slate-700 mb-6'>
				Preguntas Frecuentes
			</h3>
			<div className='space-y-3'>
				{faq.map((item, index) => {
					const isOpen = activeIndex === index;
					const normalizedCategory = item.category?.trim();
					return (
						<div
							key={item.id || index}
							className={`border border-gray-100 rounded-xl transition-all duration-200 ${
								isOpen ? "bg-secondary/30 border-secondary" : "bg-white"
							}`}
						>
							<button
								onClick={() => toggleFAQ(index)}
								className='w-full flex items-center justify-between p-4 text-left focus:outline-none group cursor-pointer'
							>
								<div className='min-w-0 pr-3'>
									{normalizedCategory ? (
										<span className='mb-1 inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-600'>
											{normalizedCategory}
										</span>
									) : null}
									<span
										className={`block font-medium transition-colors ${
											isOpen
												? "text-primary"
												: "text-slate-600 group-hover:text-primary"
										}`}
									>
										{item.question}
									</span>
								</div>
								<ChevronRight
									className={`w-5 h-5 text-slate-300 transition-transform duration-300 ${
										isOpen
											? "rotate-90 text-primary"
											: "group-hover:text-primary"
									}`}
								/>
							</button>
							<div
								className={
									"grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out " +
									(isOpen
										? "grid-rows-[1fr] opacity-100"
										: "grid-rows-[0fr] opacity-0")
								}
							>
								<div className='overflow-hidden'>
									<div className='px-4 pb-4'>
										<p className='text-slate-500 text-sm leading-relaxed border-t border-secondary/50 pt-3'>
											{item.answer}
										</p>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

// --- Componente Padre Principal ---
export const ProviderDetails = (props: ProviderDetailsProps) => {
	const {
		name,
		description,
		category,
		categoryIcon,
		categories,
		rating,
		reviews,
		faq,
		experience,
		languages,
		reviewsList,
		services,
		galleryImages,
	} = props;

	const scrollToSection = (id: string) => {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	return (
		<div className='w-full'>
			<div className='bg-white rounded-[20px] shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden'>
				<div className='flex border-b border-gray-200 overflow-x-auto scrollbar-hide sticky top-0 bg-white z-10'>
					<button
						onClick={() => scrollToSection("about")}
						className='cursor-pointer px-4 sm:px-6 py-3.5 sm:py-4 text-slate-500 hover:text-primary font-medium text-sm sm:text-base transition-colors whitespace-nowrap'
					>
						Acerca de
					</button>
					<button
						onClick={() => scrollToSection("reviews")}
						className='cursor-pointer px-4 sm:px-6 py-3.5 sm:py-4 text-slate-500 hover:text-primary font-medium text-sm sm:text-base transition-colors whitespace-nowrap'
					>
						Reseñas
					</button>
					<button
						onClick={() => scrollToSection("gallery")}
						className='cursor-pointer px-4 sm:px-6 py-3.5 sm:py-4 text-slate-500 hover:text-primary font-medium text-sm sm:text-base transition-colors whitespace-nowrap'
					>
						Galería
					</button>
					<button
						onClick={() => scrollToSection("faq")}
						className='cursor-pointer px-4 sm:px-6 py-3.5 sm:py-4 text-slate-500 hover:text-primary font-medium text-sm sm:text-base transition-colors whitespace-nowrap'
					>
						FAQ
					</button>
				</div>

				<div className='p-4 sm:p-8 space-y-8 sm:space-y-10'>
					<div id='about'>
						<AboutSection
							name={name}
							description={description}
							category={category}
							categoryIcon={categoryIcon}
							categories={categories}
							badges={props.badges}
							languages={languages}
							experience={experience}
							services={services}
						/>
					</div>

					<hr className='border-gray-100' />

					<div id='reviews'>
						<ReviewsSection
							rating={rating}
							reviews={reviews}
							reviewsList={reviewsList}
						/>
					</div>

					<hr className='border-gray-100' />

					<div id='gallery'>
						<GallerySection galleryImages={galleryImages} />
					</div>

					<hr className='border-gray-100' />

					<div id='faq'>
						<FAQSection faq={faq} />
					</div>
				</div>
			</div>
		</div>
	);
};
export type {
	FAQItem,
	ReviewItem,
} from "../../core/types/tsx/components/Profesional/ProviderDetails.types";
